import React from 'react';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';
import { SharedDialog } from '../../../common/SharedDialog';
import { surveysApi } from './api/surveysApi';
import type { SurveyAdminRecord, SurveyWriteInput } from './types';
import validationConfig from '../../../validation-config.json';

const vc = validationConfig.SURVEY_EDITOR_DIALOG;

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const textInputCss = {
  'abyss-text-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-text-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-text-input': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
    fontSize: '0.95rem',
    padding: '0 0.75rem',
  },
} as const;

interface SurveyEditorDialogProps {
  isOpen: boolean;
  survey: SurveyAdminRecord | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

const FormGrid = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const FieldError = styled(Text, {
  color: '#c0392b',
  fontSize: '0.8rem',
  margin: 0,
});

interface FormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

interface FormErrors {
  name?: string;
  startDate?: string;
  endDate?: string;
}

function validate(state: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!state.name.trim()) errors.name = vc.name.errorMessage;
  if (!state.startDate.trim()) errors.startDate = vc.startDate.errorMessage;
  if (!state.endDate.trim()) errors.endDate = vc.endDate.errorMessage;
  return errors;
}

function validateField(
  field: keyof FormState,
  state: FormState
): string | undefined {
  if (field === 'name' && !state.name.trim()) return vc.name.errorMessage;
  if (field === 'startDate' && !state.startDate.trim())
    return vc.startDate.errorMessage;
  if (field === 'endDate' && !state.endDate.trim())
    return vc.endDate.errorMessage;
  return undefined;
}

function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}

function formatDateForInput(value: string | null | undefined): string {
  if (!value) return '';
  // Handle "YYYY-MM-DD HH:mm:ss" or ISO strings
  return value.substring(0, 10);
}

export const SurveyEditorDialog = ({
  isOpen,
  survey,
  onClose,
  onSaved,
}: SurveyEditorDialogProps) => {
  const isEdit = survey !== null;
  const [form, setForm] = React.useState<FormState>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [saving, setSaving] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [addAnother, setAddAnother] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      if (survey) {
        setForm({
          name: survey.name ?? '',
          description: survey.description ?? '',
          startDate: formatDateForInput(survey.startDate),
          endDate: formatDateForInput(survey.endDate),
        });
      } else {
        setForm({ name: '', description: '', startDate: '', endDate: '' });
      }
      setErrors({});
      setTouched({});
      setSaving(false);
      setServerError(null);
      setAddAnother(false);
    }
  }, [isOpen, survey]);

  const handleChange = (field: keyof FormState) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => {
        return { ...prev, [field]: value };
      });
      // Do not mark touched on change - only on blur
      // This prevents premature error display while user is still typing/selecting
    };
  };

  const handleBlur = (field: keyof FormState) => {
    return () => {
      setTouched((prev) => {
        return { ...prev, [field]: true };
      });
      // Validate only this field when it loses focus
      // This prevents showing errors for untouched fields
      const fieldError = validateField(field, form);
      setErrors((prev) => {
        return {
          ...prev,
          [field]: fieldError,
        };
      });
    };
  };

  const handleSave = async (keepOpen: boolean) => {
    const allTouched = {
      name: true,
      description: true,
      startDate: true,
      endDate: true,
    };
    setTouched(allTouched);
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (hasErrors(validationErrors)) return;

    setSaving(true);
    setServerError(null);

    const body: SurveyWriteInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      startDate: form.startDate.trim(),
      endDate: form.endDate.trim(),
    };

    try {
      if (isEdit && survey) {
        await surveysApi.updateSurvey(survey.surveyId, body);
      } else {
        await surveysApi.createSurvey(body);
      }

      if (keepOpen) {
        setForm({ name: '', description: '', startDate: '', endDate: '' });
        setTouched({});
        setErrors({});
        setAddAnother(true);
        onSaved();
      } else {
        onSaved();
        onClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      setServerError(msg);
    } finally {
      setSaving(false);
    }
  };

  const showError = (field: keyof FormErrors) => {
    return touched[field] && errors[field];
  };

  // Check if form is currently valid (all required fields have values)
  const isFormValid = () => {
    return (
      form.name.trim() !== '' &&
      form.startDate.trim() !== '' &&
      form.endDate.trim() !== '' &&
      !isNaN(Date.parse(form.startDate)) &&
      !isNaN(Date.parse(form.endDate))
    );
  };

  return (
    <SharedDialog
      mode="app"
      ariaLabel={isEdit ? 'Update RFI' : 'Add RFI'}
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Update RFI' : 'Add RFI'}
      size="md"
      primaryAction={{
        label: saving ? 'Saving…' : isEdit ? 'Update' : 'Save',
        disabled: saving || !isFormValid(),
        onClick: () => {
          return handleSave(false);
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        variant: 'outline',
        disabled: saving,
        onClick: onClose,
      }}
      tertiaryAction={
        !isEdit
          ? {
              label: saving ? 'Saving…' : 'Save & Add Another',
              variant: 'outline',
              disabled: saving || !isFormValid(),
              onClick: () => {
                return handleSave(true);
              },
            }
          : undefined
      }
    >
      <FormGrid>
        {serverError && <FieldError>{serverError}</FieldError>}
        {addAnother && (
          <Text style={{ color: '#1a7a45', margin: 0, fontSize: '0.85rem' }}>
            RFI saved successfully. Add another below.
          </Text>
        )}

        <div>
          <TextInput
            id="survey-name"
            label="RFI Name *"
            value={form.name}
            css={textInputCss}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            isDisabled={saving}
          />
          {showError('name') && <FieldError>{errors.name}</FieldError>}
        </div>

        <div>
          <TextInput
            id="survey-description"
            label="RFI Description"
            value={form.description}
            css={textInputCss}
            onChange={handleChange('description')}
            isDisabled={saving}
          />
        </div>

        <div>
          <DateInput
            id="survey-start-date"
            label="Start Date (YYYY-MM-DD) *"
            dateFormat="YYYY-MM-DD"
            value={form.startDate}
            onChange={handleChange('startDate')}
            onBlur={handleBlur('startDate')}
            isDisabled={saving}
            errorMessage={showError('startDate') ? errors.startDate : undefined}
          />
        </div>

        <div>
          <DateInput
            id="survey-end-date"
            label="End Date (YYYY-MM-DD) *"
            dateFormat="YYYY-MM-DD"
            value={form.endDate}
            onChange={handleChange('endDate')}
            onBlur={handleBlur('endDate')}
            isDisabled={saving}
            errorMessage={showError('endDate') ? errors.endDate : undefined}
          />
        </div>
      </FormGrid>
    </SharedDialog>
  );
};
